import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Product } from '@app/@core/@models/design/product.model';
import { NamedLocation } from '@app/@core/@models/targeting/named-location-target.model';
import { TargetingForm } from '@app/@core/advertising/campaign/advert/targeting-form';
import { ConfigService } from '@app/@core/@config/config.service';
import { I18nFormatService } from '@app/@i18n/services/i18n-format.service';
import { I18nLangService } from '@app/@i18n/services/i18n-lang.service';
import { Uuid } from '@shared/utils/uuid';
import {FormControl} from "@angular/forms";
import {debounceTime} from "rxjs";

@Component({
  selector: 'app-location-targeting-options[product][targetingForm]',
  templateUrl: './named-location-targeting-options.component.html',
  styleUrls: ['./named-location-targeting-options.component.scss']
})
export class NamedLocationTargetingOptionsComponent implements OnInit, OnChanges {
  @Input() product!: Product;
  @Input() targetingForm!: TargetingForm;
  unfilteredOptions: { controlIndex: number, innerHtml: string, sub: string, type: string }[] = [];
  options: { controlIndex: number, innerHtml: string, sub: string, type: string }[] = [];
  granularOptions: { controlIndex: number, innerHtml: string, sub: string, type: string }[] = [];
  granularLinkCollapsed: boolean = true;
  showAll: boolean = true;
  searchText = "";
  uuid: string = Uuid.generate();
  searchControl = new FormControl();
  showAllControl = new FormControl(false);

  constructor(private configService: ConfigService,
              private i18nLangService: I18nLangService,
              private i18nFormatService: I18nFormatService) {
  }

  ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(100))
      .subscribe(value => {
        this.onInputChange(value);
      });

    this.showAllControl.valueChanges.subscribe(checked => {
      if (checked) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    });
  }

  onInputChange(value: any) {
    this.sortResults(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.options = [];
    this.granularOptions = [];
    const granLocations = this.configService.getNetworkData()!.moreGranularLocations || [];
    for (let i = 0; i < this.targetingForm.controls.namedLocationTargets.length; i++) {
      const namedLocation = this.getNamedLocationFromControlIndex(i);
      if (namedLocation) {
        const loc = this.translatedNamedLocation(namedLocation);
        const locationEntry = { controlIndex: i, innerHtml:  loc, sub: ((namedLocation.context || "").split(",")[0]).replace("France", "Région de France"), type: namedLocation.type};
        if (granLocations.length == 0 || granLocations.indexOf(loc) > -1) {
          this.options.push(locationEntry);
        } else {
          this.granularOptions.push(locationEntry);
        }
      }
    }
    this.showAll = true;
    this.unfilteredOptions = [...this.options];
    this.sortResults();
  }

  private normalizeText(str: string): string {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  private sortResults(pSearchText?: string) {
    if (!this.showAllControl.value && pSearchText && pSearchText.length > 0) {
      const searchText = this.normalizeText(pSearchText);
      this.options = this.unfilteredOptions.filter((o) => {
        return this.normalizeText(o.innerHtml).indexOf(searchText) > -1 || this.normalizeText(o.sub).indexOf(searchText) > -1;
      });
    } else {
      this.options = [...this.unfilteredOptions];
    }

    this.options.sort((a, b) => {
      if (a.type === b.type) {
        if (a.sub === b.sub) {
          return a.innerHtml.localeCompare(b.innerHtml);
        }
        return a.sub.localeCompare(b.sub);
      }
      return a.type === 'REGION' ? -1 : 1;
    });

    if (this.configService.getNetworkData()!.locationOrder === 'alpha') {
      this.options.sort((a, b) =>
        a.innerHtml.localeCompare(b.innerHtml)
      );
      this.granularOptions.sort((a, b) =>
        a.innerHtml.localeCompare(b.innerHtml)
      );
    }
  }

  selectedNamedLocationsInnerHtml(): string[] {
    const result: string[] = [];
    this.unfilteredOptions.forEach(option => {
      if (this.targetingForm.value.namedLocationTargets![option.controlIndex]) {
        result.push(option.innerHtml);
      }
    });
    this.granularOptions.forEach(option => {
      if (this.targetingForm.value.namedLocationTargets![option.controlIndex]) {
        result.push(option.innerHtml);
      }
    });
    return result;
  }

  private getNamedLocationFromControlIndex(index: number): NamedLocation | undefined {
    const namedLocationTarget = this.product.creativeTargeting.namedLocationTarget;
    if (namedLocationTarget && index < namedLocationTarget.locations.length) {
      return namedLocationTarget.locations[index];
    }
    return undefined;
  }

  translatedNamedLocation(namedLocation: NamedLocation): string {
    const key = 'location.' + namedLocation.name;
    if (this.i18nLangService.hasTranslationKey(key)) {
      return this.i18nFormatService.translate(key);
    } else {
      return namedLocation.name;
    }
  }
}
